import "./App.css";
import { Slide, ToastContainer } from "react-toastify";
import { Route, Routes, useLocation } from "react-router-dom";
import { SecuredRoute } from "./components/Authentication/SecuredRoute";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./hooks/storeHooks";
import { initFetchData } from "./store/thunks/initThunks";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { ApplicationWrapper } from "@idwal/idwal-react-components";
import { selectInspectionData } from "./store/selectors/inspectionSelectors";
import { cookiePreferences } from "@idwal/idwal-utilities";
import { COOKIE_CONSENT } from "@idwal/idwal-utilities/lib/constants/cookies";
import { useCookies } from "react-cookie";
import {
  DOCUMENTS_RELEASE_FLAG,
  filterNavItemsByFlags,
  getInspectionNavItems,
} from "./config/navigation";
import { useFlags } from "launchdarkly-react-client-sdk";
import Defects from "./pages/Defects";
import Documents from "./pages/Documents";
import { AdjustedGradeEditor } from "./components/Grading/AdjustedGradeEditor";
import AiSummary from "./pages/AiSummary";
import Home from "./pages/Home";
import InspectionList from "./pages/InspectionList";
import Answers from "./pages/Answers";
import Grading from "./pages/Grading";

const AUTH_URL_PROTOCOL =
  process.env.REACT_APP_ENV === "localhost" ? "http" : "https";

function App() {
  const { user } = useAuthenticator((context) => [context.user]);
  const inspection = useAppSelector(selectInspectionData);
  const dispatch = useAppDispatch();

  const [idwalSideBarOpen, setIdwalSideBarOpen] = useState(false);
  const [cookies, setCookie] = useCookies([COOKIE_CONSENT]);
  const flags = useFlags();

  const cookieUtils = cookiePreferences(setCookie, cookies);

  const location = useLocation();
  const pathParts = location.pathname.split("/");
  // Inspection routes are /inspection/<id>/... except the Checklist Review
  // route, which is /inspection/answers/<id> — so the id sits one segment
  // later there. Fall back to the selected inspection for the list route
  // (/inspection/answers) which has no id.
  const urlInspectionId =
    pathParts[1] === "inspection"
      ? pathParts[2] === "answers"
        ? pathParts[3]
        : pathParts[2]
      : pathParts[1];
  // Lower-cased because the nav links are built from this: the fallback is the
  // ASIMS inspection record, whose id is UPPER-CASE, and the inspect backend
  // keys DynamoDB on the exact string. See hooks/useInspectionId.
  const inspectionId = ((urlInspectionId || inspection?.id) ?? "").toLowerCase();
  const routePath = "/" + pathParts[1];

  useEffect(() => {
    if (user) {
      dispatch(initFetchData(user));
    }
  }, [user]);

  return (
    <ApplicationWrapper
      showNotifications={false}
      loadingNotifications={false}
      mappedNotifications={[]}
      burgerMenuLogo={"main"}
      logoutLink={`${AUTH_URL_PROTOCOL}://${process.env.REACT_APP_AUTH_URL}/logout?redirect=${window.location.href}`}
      idwalSidebarOtherItems={[
        { label: "Homepage", url: `/${inspectionId}`, icon: "pi pi-home" },
      ]}
      idwalSidebarScreeningItems={filterNavItemsByFlags(
        getInspectionNavItems(inspectionId),
        flags
      ).map(({ label, url, icon }) => ({ label, url, icon }))}
      idwalSidebarIdItems={[]}
      cookieUtils={cookieUtils}
      appSidebarItems={[]}
      activeAppSidebarItem={routePath}
      markAllNotificationsAsRead={() => {}}
      idwalSidebarOpen={idwalSideBarOpen}
      setIdwalSideBarOpen={(open: boolean) => setIdwalSideBarOpen(open)}
    >
      <>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          pauseOnHover
          theme="colored"
          transition={Slide}
        />
        <Routes>
          <Route path="/" element={<SecuredRoute />}>
            <Route
              path="/"
              element={
                <a href="https://www.idwalmarine.com">
                  <img id="idwal-logo" src="/Idwal-Logo.svg" alt="logo" />
                </a>
              }
            />
          </Route>
          <Route path={":inspectionId"} element={<SecuredRoute />}>
            <Route index element={<Home />} />
          </Route>
          <Route path={"/inspection"} element={<SecuredRoute />}>
            <Route path=":inspectionId/defects" element={<Defects />} />
            {flags[DOCUMENTS_RELEASE_FLAG] && (
              <Route path=":inspectionId/documents" element={<Documents />} />
            )}
            <Route
              path=":inspectionId/adjusted"
              element={<AdjustedGradeEditor />}
            />
            <Route path=":inspectionId/grading" element={<Grading />} />
            <Route path=":inspectionId/ai-summary" element={<AiSummary />} />
            <Route path="answers" element={<InspectionList />} />
            <Route path="answers/:inspectionId" element={<Answers />} />
          </Route>
        </Routes>
      </>
    </ApplicationWrapper>
  );
}

export default App;
